import { useContext } from 'react';
import { NewInvoiceForm } from './NewInvoiceForm';
import { Box, Stack, Typography } from '@mui/material';
import {
  CustomMetaTitle,
  LinkArrowLeft2,
  LinkReturnToMain,
  NoDataFound2,
} from 'Components';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { InvoiceList } from './InvoiceList';
import { GettingStartedSection } from './GettingStartedSection';
import { CustomDialog } from 'Components/CustomDialog';
import { FormTitle } from 'Modules/Reimbursements/FormTitle';
import { FormStepper } from 'Modules/Reimbursements/FormStepper';
import { AdditionalDetailsForm } from 'Modules/Reimbursements/AdditionalDetailsForm';
import { StatementForm } from 'Modules/Reimbursements/StatementForm';
import { CannabisContext, IInvoice } from 'Context/CannabisContext';
import { IFormData } from 'Interfaces/reimbursements';
import { OcrLoading } from './NewInvoiceForm/OcrLoading';

export enum STEP {
  INVOICE_UPLOAD = 1,
  ADDITIONAL_DETAILS,
  STATEMENT,
}

export interface ICannabisFormData extends IFormData {
  invoices: IInvoice[];
}

export const CannabisMain = () => {
  const {
    showUploadFileArea,
    currentStep,
    formData,
    showDialog,
    dialogContent,
    isEligible,
    internetReqNum,
    isLoading,
    loader,
    onClickReturnToMain,
    getDialogOnClose,
    redirects,
    isOcrLoading,
  } = useContext(CannabisContext);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();

  const getLoader = () => (isOcrLoading ? <OcrLoading /> : loader);

  return (
    <Stack
      gap={isDesktop ? '32px' : '24px'}
      marginBottom={'80px'}
      maxWidth={isEligible ? '1000px' : '100%'}
    >
      <CustomMetaTitle title={dictionary.titleCannabis} />
      <LinkReturnToMain
        onClick={
          formData.invoices.length === 0 ? undefined : onClickReturnToMain
        }
      />
      {isLoading || isOcrLoading ? (
        getLoader()
      ) : isEligible ? (
        <>
          <FormTitle />
          <Stack
            sx={{
              maxWidth: '400px',
              p: '16px 16px 25px 16px',
              boxShadow: '-2px -2px 16px 0px rgba(8, 30, 84, 0.08)',
              borderRadius: '16px',
              backgroundColor: '#fff',
              position: 'relative',
              margin: '0 auto',
              top: isDesktop ? '35px' : '17px',
              zIndex: '0',
            }}
          >
            <FormStepper
              steps={[{ name: '' }, { name: '' }, { name: '' }]}
              activeStepIndex={currentStep - 1}
            />
          </Stack>
          <Stack
            sx={{
              backgroundColor: '#fff',
              maxWidth: '400px',
              height: '30px',
              margin: '0 auto',
              position: 'relative',
              top: isDesktop ? '-20px' : '-36px',
              zIndex: '2',
            }}
          ></Stack>
          <Stack
            sx={{
              padding: isDesktop ? '40px 24px 48px 24px' : '16px',
              borderRadius: '16px',
              backgroundColor: '#fff',
              boxShadow: '-2px -2px 16px 0px rgba(8, 30, 84, 0.08)',
              zIndex: '1',
              position: 'relative',
              top: '-70px',
            }}
          >
            {currentStep === STEP.INVOICE_UPLOAD && (
              <>
                <Typography variant='h3' component='h2' m={'0 auto'}>
                  {dictionary.addInvoice}
                </Typography>
                {!showUploadFileArea && formData.invoices.length > 0 ? (
                  <InvoiceList />
                ) : (
                  <GettingStartedSection />
                )}
              </>
            )}
            {currentStep === STEP.ADDITIONAL_DETAILS && (
              <AdditionalDetailsForm />
            )}
            {currentStep === STEP.STATEMENT && <StatementForm />}
          </Stack>

          <NewInvoiceForm />

          <CustomDialog
            showDialog={showDialog}
            onclose={getDialogOnClose}
            width={isDesktop ? '770px' : '90vw'}
            height='auto'
            mt={isDesktop ? undefined : internetReqNum ? '140px' : '100px'}
            isScroll={!isDesktop}
          >
            {dialogContent}
          </CustomDialog>
        </>
      ) : (
        <NoDataFound2
          gap={isDesktop ? '48px' : '32px'}
          redirects={redirects}
          headerComponent={
            <Stack alignItems={'center'} gap={'4px'}>
              <Typography
                variant='h2'
                color='text.secondary'
                textAlign={'center'}
                maxWidth={isDesktop ? undefined : '320px'}
              >
                {dictionary.noEntitle.cannabis.title}
              </Typography>
              <Typography
                variant={'body3'}
                color='text.secondary'
                mt={'12px'}
                textAlign={'center'}
                maxWidth={isDesktop ? undefined : '235px'}
              >
                {dictionary.noEntitle.cannabis.subtitle}
              </Typography>
              <LinkArrowLeft2
                text={dictionary.noEntitle.cannabis.moreInfo}
                href={
                  'https://shikum.mod.gov.il/medical/Medicine/cannabis-license'
                }
                newWindow={true}
                variant='body3'
              />
            </Stack>
          }
        />
      )}
    </Stack>
  );
};
